/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { IFittingPrioritizationFacade } from '../../../../../data-access/src/lib/domain/facades-models/fitting-prioritization-facade-interface';
import { BackendService, LocalStorageService } from '../../../../../utils/src';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { GlobalConstants } from '../../constants/global-constants';
import { SystemInfo } from '../../../../../data-access/src/lib/domain/system-info';
import { from, Observable, take } from 'rxjs';
import { FittingPrioritization } from '../../../../../data-access/src/lib/domain/fitting-prioritization-types';
import { FittingPrioritizationReturnCodes } from '../../models/fitting-prioritization-return-codes-types';

@Injectable({
  providedIn: 'root',
})
export class FittingPrioritizationFacade implements IFittingPrioritizationFacade {
  constructor(
    public backendService: BackendService,
    private localStorageService: LocalStorageService
  ) {}

  public closeWindow(): void {
    this.backendService.onCloseWindow().pipe(take(1)).subscribe();
  }

  public resizeWindowHeight(height: number): void {
    if (!!height) {
      this.backendService.changeDialogHeight(height).pipe(take(1)).subscribe();
    }
  }

  public getState(): SysqueElectricalState {
    return this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY) as SysqueElectricalState;
  }

  public getSystems(): Observable<SystemInfo[]> {
    return from<Promise<SystemInfo[]>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.GetListOfSystems)
    );
  }

  public getMainShape(systemName: string): Observable<string> {
    return from<Promise<string>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.GetMainShape, [systemName])
    );
  }

  public getFittingTypes(systemName: string, shape: string): Observable<string[]> {
    return from<Promise<string[]>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.GetFittingTypes, [systemName, shape])
    );
  }

  public getItemsBySystemAndFittingType(system: string, fittingType: string): Observable<FittingPrioritization[]> {
    return from<Promise<FittingPrioritization[]>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.GetFittingsBySystemAndFittingType, [
        system,
        fittingType,
      ])
    );
  }

  public reorderFittings(
    reorderItems: FittingPrioritization[],
    systemName: string,
    shape: string
  ): Observable<FittingPrioritizationReturnCodes> {
    return from<Promise<FittingPrioritizationReturnCodes>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.ReorderFittings, [
        reorderItems,
        systemName,
        shape,
      ])
    );
  }

  public updateLocalStorage(key: string, value: any): void {
    this.localStorageService.updateDispatchLocalStorage(key, value);
  }
}
